import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../utility/helper';
import { ctxParams, rupee, formatDateTime } from '../utility/instituteHelpers';
import InstituteDataTable, { esc, badgeHtml } from '../utility/InstituteDataTable';
import '../assets/css/Institute.css';

const money = (d, t) => (t === 'display' ? rupee(d) : (d ?? ''));

const approvalColumns = [
  { title: 'ID', data: 'approval_id' },
  {
    title: 'Institute', data: 'institute_name',
    render: (d, t, r) => (t === 'display'
      ? `${esc(d)}<br/><small class="text-muted">${esc(r.institute_location)}</small>`
      : `${d ?? ''} ${r.institute_location ?? ''}`),
  },
  {
    title: 'Batch', data: 'batch_name',
    render: (d, t, r) => (t === 'display'
      ? `${esc(d)}<br/><small class="text-muted">${esc(r.batch_code)}</small>`
      : `${d ?? ''} (${r.batch_code ?? ''})`),
  },
  { title: 'Requested', data: 'requested_fee', render: money },
  { title: 'Approved (at request)', data: 'approved_fee_at_request', render: money },
  { title: 'Difference', data: 'fee_difference', render: money },
  { title: 'Requested At', data: 'requested_at', render: (d, t) => (t === 'display' ? formatDateTime(d) : (d ?? '')) },
  { title: 'Status', data: 'status', render: (d, t) => (t === 'display' ? badgeHtml(d) : (d ?? '')) },
  { title: 'Remarks', data: 'remarks', render: (d) => esc(d) || '—' },
  {
    title: 'Action', data: null, orderable: false, searchable: false, notexport: true,
    render: (d, t, r) => (r.status === 'Pending'
      ? `<select class="form-select form-select-sm" style="min-width:130px" data-action="review" data-key="${esc(r.approval_id)}">
           <option value="">-- Select --</option><option value="approve">Approve</option><option value="reject">Reject</option>
         </select>`
      : `<span class="text-muted">Reviewed ${r.reviewed_at ? esc(formatDateTime(r.reviewed_at)) : ''}</span>`),
  },
];

const BatchFeeApprovals = () => {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchApprovals = async () => {
    try {
      const params = { ...ctxParams() };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${baseurl}/admin/batch-fee-approvals`, { params });
      setRows(res.data || []);
    } catch (err) {
      console.error('Error fetching approvals:', err);
      if (err.response?.status === 403) {
        Swal.fire('Forbidden', 'Super Admin access only.', 'error');
      }
    }
  };

  useEffect(() => { fetchApprovals(); /* eslint-disable-next-line */ }, [statusFilter]);

  const review = async (row, decision) => {
    const { value: remarks, isConfirmed } = await Swal.fire({
      title: decision === 'approve' ? 'Approve batch fee?' : 'Reject batch fee?',
      html: `<div style="text-align:left">
        <b>${row.institute_name}</b> — ${row.batch_name} (${row.batch_code})<br/>
        Requested: <b>${rupee(row.requested_fee)}</b> · Approved: ${rupee(row.approved_fee_at_request)}
        · Diff: ${rupee(row.fee_difference)}
      </div>`,
      input: 'textarea',
      inputPlaceholder: 'Remarks (optional)',
      showCancelButton: true,
      confirmButtonText: decision === 'approve' ? 'Approve' : 'Reject',
      confirmButtonColor: decision === 'approve' ? '#16a34a' : '#dc2626',
    });
    if (!isConfirmed) return;
    try {
      await axios.post(`${baseurl}/admin/batch-fee-approvals/${row.approval_id}/${decision}`, {
        remarks: remarks || null,
        ...ctxParams(),
      });
      Swal.fire('Done', `Batch fee ${decision === 'approve' ? 'approved' : 'rejected'}.`, 'success');
      fetchApprovals();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Action failed', 'error');
    }
  };

  return (
    <div className="content container-fluid inst-wrap">
      <center><h2><b>Batch Fee Approvals</b></h2></center>

      <div className="inst-card">
        <div className="mb-3">
          <label className="form-label me-2">Status</label>
          <select className="form-select d-inline-block" style={{ maxWidth: 200 }}
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>

        <InstituteDataTable
          data={rows}
          rowKey="approval_id"
          columns={approvalColumns}
          order={[[0, 'desc']]}
          filename="Mensa_Batch_Fee_Approvals"
          emptyText="No approval requests"
          onAction={(action, row, value) => action === 'review' && review(row, value)}
        />
      </div>
    </div>
  );
};

export default BatchFeeApprovals;
